import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/api'; 

const AssinaturaAcordo = () => {
    // 1. Mudamos de 'id' para 'token' para bater com a nova rota segura
    const { token } = useParams(); 
    
    const [acordo, setAcordo] = useState(null);
    const [loadingInfo, setLoadingInfo] = useState(true);
    const [signing, setSigning] = useState(false);
    const [erro, setErro] = useState('');
    const [sucesso, setSucesso] = useState(false);

    useEffect(() => {
        // 2. Agora buscamos os detalhes usando o Token UUID
        api.get(`/public/acordos/${token}`)
            .then(res => {
                setAcordo(res.data);
                setLoadingInfo(false);
            })
            .catch(err => {
                console.error(err);
                setErro('Este link de acordo é inválido ou já expirou.');
                setLoadingInfo(false);
            });
    }, [token]);

    const handleAssinar = () => {
        setErro('');
        setSigning(true);

        if (!navigator.geolocation) {
            setErro("Seu navegador não suporta geolocalização.");
            setSigning(false);
            return;
        }

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const coords = {
                    latitude: position.coords.latitude.toString(),
                    longitude: position.coords.longitude.toString()
                };

                try {
                    // 3. Enviamos a assinatura usando o Token UUID
                    await api.post(`/public/acordos/${token}/assinar`, coords);
                    setSucesso(true);
                } catch (error) {
                    console.error("Erro ao assinar:", error);
                    setErro(error.response?.data || "Erro ao registrar assinatura.");
                } finally {
                    setSigning(false);
                }
            },
            (error) => {
                setSigning(false);
                setErro("A localização é obrigatória para assinar o acordo.");
            },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };
    
    if (loadingInfo) {
        return <div style={styles.container}><p style={styles.text}>Carregando os termos do acordo...</p></div>;
    }

    if (!acordo && !loadingInfo) {
        return (
            <div style={styles.container}>
                <div style={styles.card}>
                    <h2 style={{color: '#ef4444'}}>Link Inválido</h2>
                    <p style={styles.text}>{erro}</p>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                
                {/* CABEÇALHO */}
                <div style={styles.header}>
                    <h2 style={styles.title}>Proposta de Acordo</h2>
                    <p style={styles.subtitle}>Confira os termos antes de assinar digitalmente.</p>
                </div>

                {sucesso ? (
                    // TELA DE SUCESSO (Próximo passo: Mostrar o PIX aqui)
                    <div style={styles.successBox}>
                        <h3 style={{color: '#10b981', fontSize: '1.5rem', marginBottom: '10px'}}>✓ Acordo Assinado!</h3>
                        <p style={styles.text}>Sua assinatura digital foi registrada com sucesso.</p>
                        <p style={styles.text}>O QR Code para pagamento será exibido abaixo (em breve).</p>
                    </div>
                ) : (
                    // TELA DO CONTRATO
                    <>
                        <div style={styles.contractBox}>
                            <p style={styles.contractItem}>
                                <strong>Credor:</strong> {acordo.credorNome}
                            </p>
                            <p style={styles.contractItem}>
                                <strong>Devedor:</strong> {acordo.devedorNome}
                            </p>
                            <p style={styles.contractItem}>
                                <strong>Referente a:</strong> {acordo.descricaoDivida}
                            </p>
                            <hr style={styles.divider} />
                            <p style={styles.contractItem}>
                                <strong>Valor Acordado:</strong> <span style={styles.highlight}>R$ {acordo.valorAcordado.toFixed(2)}</span>
                            </p>
                            <p style={styles.contractItem}>
                                <strong>Condição:</strong> {acordo.parcelas === 1 ? 'À vista (PIX)' : `${acordo.parcelas}x parcelado`}
                            </p>
                        </div>

                        {/* AVISO LEGAL */}
                        <div style={styles.legalBox}>
                            <p style={styles.legalText}>
                                Ao clicar em "Li e Aceito", você reconhece a dívida descrita acima e concorda com os termos de pagamento. 
                                Para fins de validade jurídica, registraremos seu endereço de IP, modelo do dispositivo e geolocalização.
                            </p>
                        </div>

                        {erro && <p style={styles.errorMsg}>{erro}</p>}

                        <button 
                            onClick={handleAssinar} 
                            disabled={signing} 
                            style={{
                                ...styles.button, 
                                opacity: signing ? 0.7 : 1,
                                cursor: signing ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {signing ? 'Obtendo GPS e Assinando...' : 'Li e Aceito os Termos'}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

const styles = {
    container: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#020617',
        padding: '20px',
        fontFamily: 'system-ui, -apple-system, sans-serif'
    },
    card: {
        background: 'rgba(255, 255, 255, 0.03)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        padding: 'clamp(20px, 5vw, 40px)',
        borderRadius: '16px',
        maxWidth: '500px',
        width: '100%',
        backdropFilter: 'blur(10px)',
        boxSizing: 'border-box'
    },
    header: {
        textAlign: 'center',
        marginBottom: '25px'
    },
    title: {
        color: 'white',
        fontSize: '1.8rem',
        margin: '0 0 10px 0'
    },
    subtitle: {
        color: '#94a3b8',
        margin: 0,
        fontSize: '0.95rem'
    },
    text: {
        color: '#f8fafc',
        textAlign: 'center',
        lineHeight: '1.5'
    },
    contractBox: {
        background: 'rgba(0, 0, 0, 0.2)',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px',
        border: '1px solid rgba(255, 255, 255, 0.05)'
    },
    contractItem: {
        color: '#f8fafc',
        margin: '10px 0',
        fontSize: '1rem',
        display: 'flex',
        justifyContent: 'space-between'
    },
    divider: {
        borderColor: 'rgba(255, 255, 255, 0.1)',
        margin: '15px 0'
    },
    highlight: {
        color: '#10b981',
        fontWeight: 'bold',
        fontSize: '1.2rem'
    },
    legalBox: {
        background: 'rgba(245, 158, 11, 0.1)', // Fundo âmbar bem suave
        borderLeft: '4px solid #f59e0b',
        padding: '12px 15px',
        marginBottom: '20px',
        borderRadius: '0 4px 4px 0'
    },
    legalText: {
        color: '#fbbf24',
        fontSize: '0.8rem',
        lineHeight: '1.5',
        margin: 0,
        textAlign: 'justify'
    },
    errorMsg: {
        color: '#ef4444',
        fontSize: '0.9rem',
        textAlign: 'center',
        background: 'rgba(239, 68, 68, 0.1)',
        padding: '10px',
        borderRadius: '8px',
        marginBottom: '15px'
    },
    successBox: {
        textAlign: 'center',
        padding: '30px 0'
    },
    button: {
        width: '100%',
        background: '#10b981',
        color: '#022c22',
        padding: '16px',
        borderRadius: '8px',
        border: 'none',
        fontWeight: 'bold',
        fontSize: '1.1rem',
        transition: '0.3s'
    }
};

export default AssinaturaAcordo;